import { Routes, Route, Navigate } from 'react-router-dom';
import { SupportDashboard } from './SupportDashboard';
import { TicketList } from './tickets/TicketList';
import { KnowledgeBase } from './knowledge/KnowledgeBase';
import { LiveChat } from './chat/LiveChat';
import { VisitorManagement } from './visitors/VisitorManagement';
import { SupportReports } from './reports/SupportReports';
import { SupportSettings } from './settings/SupportSettings';
import { SupportAuditLog } from './audit/SupportAuditLog';
import { ITSMAssetDetailView } from '../assets/assets/AssetDetailView';
import { useRbacStore } from '../../store/rbacStore';

export function SupportHub() {
  // Feature-page access is grant-driven (module 'Support'), matching the sidebar
  // and the admin roles matrix. Everyone who can open the hub sees the dashboard
  // (role-aware: employees get a personal view). The admin-only pages below need
  // the corresponding Company-scope grant; otherwise the user is sent to their
  // tickets. This replaces the old role-name checks (employeeOnly/agentOnly/vendor).
  const getScope = useRbacStore((s) => s.getScope);
  useRbacStore((s) => s.permissions); // re-evaluate when access loads/changes
  useRbacStore((s) => s.roles);
  const canAudit = getScope('Support', 'Support Audit Log', 'View') === 'Company';
  const canReports = getScope('Support', 'Support Reports', 'View') === 'Company';
  const canSettings = getScope('Support', 'Ticket Settings', 'Edit') === 'Company';
  const toTickets = <Navigate to="/support/tickets" replace />;

  return (
    <div className="support-hub h-full w-full flex-1 flex flex-col min-h-0">
      <Routes>
        {/* Dashboard — role-aware: employees see only their own tickets */}
        <Route index element={<SupportDashboard />} />

        {/* Ticketing */}
        <Route path="/tickets" element={<TicketList />} />
        <Route path="/tickets/new" element={<TicketList />} />
        <Route path="/tickets/:id" element={<TicketList />} />

        {/* Support Asset Registry Direct Number URLs */}
        <Route path="/assets/:registryNumber" element={<ITSMAssetDetailView />} />
        <Route path="/assets/:registryNumber/invoice" element={<ITSMAssetDetailView autoOpenTab="invoice" />} />
        <Route path="/assets/:registryNumber/invocie" element={<ITSMAssetDetailView autoOpenTab="invoice" />} />
        <Route path="/assets/:registryNumber/gallery" element={<ITSMAssetDetailView autoOpenTab="gallery" />} />
        <Route path="/assets/:registryNumber/warranty" element={<ITSMAssetDetailView autoOpenTab="warranty" />} />

        {/* Live Chat AI Assistant */}
        <Route path="/chat" element={<LiveChat />} />

        {/* Knowledge Base */}
        <Route path="/knowledge" element={<KnowledgeBase />} />

        {/* Audit Log / History — needs Support Audit Log › View (Company) */}
        <Route path="/history" element={canAudit ? <SupportAuditLog /> : toTickets} />
        <Route path="/audit" element={canAudit ? <SupportAuditLog /> : toTickets} />

        {/* Reports — needs Support Reports › View (Company) */}
        <Route path="/reports" element={canReports ? <SupportReports /> : toTickets} />

        {/* SLA + Settings / Admin — needs Ticket Settings › Edit (Company) */}
        <Route path="/sla" element={canSettings ? <SupportSettings defaultTab="sla" /> : toTickets} />
        <Route path="/settings" element={canSettings ? <SupportSettings /> : toTickets} />
        <Route path="/admin" element={canSettings ? <SupportSettings /> : toTickets} />

        {/* Visitor Management */}
        <Route path="/visitors" element={<VisitorManagement />} />
      </Routes>
    </div>
  );
}

export default SupportHub;
